import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Search, Plus, Phone, MapPin, ChevronRight,
  Users, Pencil, Trash2, Calendar, CheckSquare, Square, X, ShoppingBag
} from "lucide-react";
import { getClients, deleteClient, bulkDeleteClients } from "@/utils/api";
import ClientForm from "@/components/ClientForm";
import AppointmentScheduler from "@/components/AppointmentScheduler";
import { toast } from "sonner";

const FREQ_LABEL = {
  1: "Ogni settimana", 2: "Ogni 2 sett.", 3: "Ogni 3 sett.",
  4: "Ogni mese", 6: "Ogni 6 sett.", 8: "Ogni 2 mesi", 12: "Ogni 3 mesi",
};
const DAY_LABEL = { 0: "Lun", 1: "Mar", 2: "Mer", 3: "Gio", 4: "Ven", 5: "Sab", 6: "Dom" };

export default function Clients() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editClient, setEditClient] = useState(null);
  const [scheduleClient, setScheduleClient] = useState(null);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getClients();
      setClients(res.data);
    } catch {
      toast.error("Errore nel caricamento clienti");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = clients.filter((c) =>
    search === "" ||
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    (c.company || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.zone || "").toLowerCase().includes(search.toLowerCase()) ||
    (c.city || "").toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Eliminare ${name} e tutti i suoi appuntamenti?`)) return;
    try {
      await deleteClient(id);
      toast.success("Cliente eliminato");
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(filtered.map((c) => c.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    const names = clients.filter((c) => selectedIds.has(c.id)).map((c) => c.company || c.name);
    const preview = names.slice(0, 3).join(", ") + (names.length > 3 ? ` e altri ${names.length - 3}` : "");
    if (!window.confirm(`Eliminare ${selectedIds.size} clienti (${preview}) e tutti i loro appuntamenti?\n\nQuesta azione non può essere annullata.`)) return;
    try {
      await bulkDeleteClients([...selectedIds]);
      toast.success(`${selectedIds.size} clienti eliminati`);
      exitSelectMode();
      load();
    } catch {
      toast.error("Errore nell'eliminazione");
    }
  };

  const zones = [...new Set(filtered.map((c) => c.zone || "Senza zona"))].sort();

  return (
    <div className="p-4 md:p-6 space-y-5 fade-in">

      {/* Header */}
      {selectMode ? (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <button data-testid="exit-select-mode" onClick={exitSelectMode}
              className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
              <X className="w-5 h-5" />
            </button>
            <span className="font-semibold text-[#0F172A] text-sm">
              {selectedIds.size > 0 ? `${selectedIds.size} selezionati` : "Seleziona clienti"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="select-all-btn"
              onClick={selectedIds.size === filtered.length ? deselectAll : selectAll}
              className="text-xs text-blue-600 font-semibold px-2 py-1 rounded hover:bg-blue-50">
              {selectedIds.size === filtered.length ? "Nessuno" : "Tutti"}
            </button>
            {selectedIds.size > 0 && (
              <button data-testid="bulk-delete-btn" onClick={handleBulkDelete}
                className="flex items-center gap-1.5 bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm font-semibold hover:bg-red-600 active:scale-95 transition-all">
                <Trash2 className="w-3.5 h-3.5" />
                Elimina ({selectedIds.size})
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-[#0F172A]">Clienti</h1>
            <p className="text-sm text-slate-500">{clients.length} clienti totali</p>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="select-mode-btn" onClick={() => setSelectMode(true)}
              className="flex items-center gap-1.5 border border-slate-200 text-slate-600 px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-50 transition-colors active:scale-95">
              <CheckSquare className="w-4 h-4" />
              Seleziona
            </button>
            <button data-testid="add-client-btn"
              onClick={() => { setEditClient(null); setShowForm(true); }}
              className="flex items-center gap-1.5 bg-[#0F172A] text-white px-3 py-2 rounded-lg text-sm font-semibold hover:bg-slate-800 transition-colors active:scale-95">
              <Plus className="w-4 h-4" />
              Nuovo
            </button>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input data-testid="client-search" placeholder="Cerca per nome, azienda, zona..."
          value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-white" />
      </div>

      {/* Clients list */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-20 w-full rounded-xl" />)}
        </div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 py-14 flex flex-col items-center text-center px-5">
          <Users className="w-10 h-10 text-slate-200 mb-3" />
          <p className="font-medium text-slate-600">
            {search ? "Nessun cliente trovato" : "Nessun cliente ancora"}
          </p>
          {!search && (
            <button onClick={() => setShowForm(true)}
              className="mt-3 text-sm text-blue-600 font-medium hover:underline">
              Aggiungi il primo cliente
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-5">
          {zones.map((zone) => {
            const zoneClients = filtered.filter((c) => (c.zone || "Senza zona") === zone);
            return (
              <div key={zone}>
                <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2 px-1">
                  {zone} ({zoneClients.length})
                </div>
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
                  {zoneClients.map((client, idx) => (
                    <ClientRow
                      key={client.id}
                      client={client}
                      isLast={idx === zoneClients.length - 1}
                      selectMode={selectMode}
                      selected={selectedIds.has(client.id)}
                      onToggleSelect={() => toggleSelect(client.id)}
                      onView={() => !selectMode && navigate(`/app/clienti/${client.id}`)}
                      onEdit={() => { setEditClient(client); setShowForm(true); }}
                      onDelete={() => handleDelete(client.id, client.name)}
                      onSchedule={() => setScheduleClient(client)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <ClientForm open={showForm} onClose={() => setShowForm(false)} onSuccess={load} client={editClient} />
      <AppointmentScheduler
        open={!!scheduleClient} onClose={() => setScheduleClient(null)}
        onSuccess={() => toast.success("Appuntamento aggiunto")}
        preselectedClientId={scheduleClient?.id}
      />
    </div>
  );
}

function ClientRow({ client, isLast, selectMode, selected, onToggleSelect, onView, onEdit, onDelete, onSchedule }) {
  const freqLabel = FREQ_LABEL[client.frequency_weeks] || `Ogni ${client.frequency_weeks} sett.`;
  const dayLabel = client.preferred_day != null ? DAY_LABEL[client.preferred_day] : null;

  return (
    <div
      data-testid={`client-row-${client.id}`}
      onClick={selectMode ? onToggleSelect : undefined}
      className={`flex items-center gap-3 px-4 py-3.5 transition-colors stagger-item slide-up
        ${!isLast ? "border-b border-slate-100" : ""}
        ${selectMode ? "cursor-pointer" : "hover:bg-slate-50"}
        ${selected ? "bg-blue-50" : ""}
      `}
    >
      {/* Checkbox o Avatar */}
      {selectMode ? (
        <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center">
          {selected
            ? <CheckSquare className="w-5 h-5 text-blue-600" data-testid={`check-${client.id}`} />
            : <Square className="w-5 h-5 text-slate-300" data-testid={`uncheck-${client.id}`} />
          }
        </div>
      ) : (
        <div className="w-9 h-9 rounded-full bg-[#0F172A] flex-shrink-0 flex items-center justify-center">
          <span className="text-white text-sm font-bold">
            {(client.company || client.name).charAt(0).toUpperCase()}
          </span>
        </div>
      )}

      {/* Info */}
      <div className="flex-1 min-w-0" onClick={!selectMode ? onView : undefined}
        style={{ cursor: selectMode ? "inherit" : "pointer" }}>
        <div className="font-semibold text-sm text-[#0F172A] truncate">
          {client.company || client.name}
        </div>
        {client.last_order_date && (
          <div className="flex items-center gap-0.5 text-[10px] text-violet-500 font-medium mt-0.5" data-testid={`last-order-${client.id}`}>
            <ShoppingBag className="w-2.5 h-2.5 flex-shrink-0" />
            Ult. ordine: {new Date(client.last_order_date).toLocaleDateString("it-IT", { day: "numeric", month: "short", year: "2-digit" })}
          </div>
        )}
        <div className="text-xs text-slate-400 truncate flex items-center gap-1.5">
          {client.company && <span>{client.name} · </span>}
          <span>{freqLabel}</span>
          {dayLabel && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 font-semibold text-[10px]">
              {dayLabel}
            </span>
          )}
        </div>
        {client.notes && (
          <div className="mt-1 flex items-center gap-1 text-[11px] text-amber-700 bg-amber-50 rounded px-1.5 py-0.5 max-w-xs truncate">
            <span className="flex-shrink-0">&#128161;</span>
            <span className="truncate">{client.notes}</span>
          </div>
        )}
        <div className="flex gap-2 mt-0.5">
          {client.phone && (
            <a href={`tel:${client.phone}`} onClick={(e) => e.stopPropagation()}
              className="flex items-center gap-0.5 text-[10px] text-slate-400 hover:text-blue-500"
              data-testid={`client-phone-${client.id}`}>
              <Phone className="w-2.5 h-2.5" />{client.phone}
            </a>
          )}
          {client.city && (
            <span className="flex items-center gap-0.5 text-[10px] text-slate-400">
              <MapPin className="w-2.5 h-2.5" />{client.city}
            </span>
          )}
        </div>
      </div>

      {/* Actions (solo fuori dalla modalità selezione) */}
      {!selectMode && (
        <div className="flex items-center gap-1 flex-shrink-0">
          <button data-testid={`schedule-btn-${client.id}`} onClick={onSchedule}
            className="p-2 text-slate-400 hover:text-blue-500 transition-colors rounded-lg hover:bg-blue-50" title="Programma visita">
            <Calendar className="w-4 h-4" />
          </button>
          <button data-testid={`edit-client-${client.id}`} onClick={onEdit}
            className="p-2 text-slate-400 hover:text-[#0F172A] transition-colors rounded-lg hover:bg-slate-100" title="Modifica">
            <Pencil className="w-4 h-4" />
          </button>
          <button data-testid={`delete-client-${client.id}`} onClick={onDelete}
            className="p-2 text-slate-400 hover:text-red-500 transition-colors rounded-lg hover:bg-red-50" title="Elimina">
            <Trash2 className="w-4 h-4" />
          </button>
          <button data-testid={`view-client-${client.id}`} onClick={onView}
            className="p-2 text-slate-400 hover:text-[#0F172A] transition-colors rounded-lg hover:bg-slate-100">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
}
